import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Header } from "../../Header";
import { useLocation } from "react-router-dom";
import './Posdayend.css';
import { local_signage, local_url } from "../../Const";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Autocomplete, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

export const Posdayend = () => {
  const location = useLocation();
  
  // State variables for filters
  const [sfAreaId, setSfAreaId] = useState("");
  const [businessDate, setBusinessDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(dayjs().subtract(31, 'day'));
  const [toDate, setToDate] = useState(dayjs());
  
  // State variables for data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [filterText, setFilterText] = useState('');
  
  // Pagination state variables
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  
  // State variables for dropdowns - NEW: Add segments, areas, branches
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  const [sfAreas, setSfAreas] = useState([]);

  // Get authentication token and user ID from localStorage
  const token = localStorage.getItem('AuthorizationToken');
  const userId = localStorage.getItem('userId');

  // Handle authentication error
  const handleAuthError = () => {
    setError("Authentication error. Please log in again.");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // Format date for API calls (YYYYMMDD format)
  const formatDateForApi = (date) => {
    if (!date) return "";
    
    let formattedDate;
    
    if (typeof date === 'string') {
      formattedDate = new Date(date);
    } else {
      formattedDate = date;
    }
    
    if (formattedDate instanceof Date && !isNaN(formattedDate.getTime())) {
      const year = formattedDate.getFullYear();
      const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
      const day = String(formattedDate.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    }
    
    return "";
  };

  // Format date for display (DD-MM-YYYY)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid and not the SQL min date
      if (isNaN(date.getTime()) || 
          dateString === "1723-01-01T00:00:00" || 
          dateString === "1753-01-01T00:00:00" ||
          dateString.includes("0001-01-01")) {
        return ""; 
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  };

  // Format time for display (HH:MM)
  const formatDisplayTime = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return "";
      }
      
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${hours}:${minutes}`;
    } catch (e) {
      console.error("Error formatting time:", e);
      return "";
    }
  };

  // Handle business date change
  const handleDateChange = (newDate) => {
    setBusinessDate(newDate ? newDate.toDate() : new Date());
  };

  // Handle from date change with 7-day validation
  const handleFromDateChange = (newDate) => {
    setFromDate(newDate);
    
    // Validate 7-day range when from date changes
    if (newDate && toDate) {
      const daysDifference = toDate.diff(newDate, 'day');
      if (daysDifference > 31) {
        setError('Date range cannot exceed 31 days. Please select a shorter date range.');
      } else if (newDate.isAfter(toDate)) {
        setError('From Date cannot be after To Date.');
      } else {
        setError(null); // Clear error if validation passes
      }
    }
  };

  // Handle to date change with 7-day validation
  const handleToDateChange = (newDate) => {
    setToDate(newDate);
    
    // Validate 7-day range when to date changes
    if (fromDate && newDate) {
      const daysDifference = newDate.diff(fromDate, 'day');
      if (daysDifference > 31) {
        setError('Date range cannot exceed 31 days. Please select a shorter date range.');
      } else if (fromDate.isAfter(newDate)) {
        setError('From Date cannot be after To Date.');
      } else {
        setError(null); // Clear error if validation passes
      }
    }
  };

  // Check if date range is valid (not exceeding 7 days)
  const isDateRangeValid = () => {
    if (!fromDate || !toDate) return false;
    const daysDifference = toDate.diff(fromDate, 'day');
    return daysDifference <= 31 && !fromDate.isAfter(toDate);
  };

  // Date disable logic for From Date picker - Allow any date
  const shouldDisableFromDate = (date) => {
    // No restrictions on from date - user can select any date
    return false;
  };

  // Date disable logic for To Date picker
  const shouldDisableToDate = (date) => {
    // If fromDate is selected, apply restrictions based on fromDate
    if (fromDate) {
      const daysDifference = date.diff(fromDate, 'day');
      // Disable dates that would create >7 day range
      if (daysDifference > 31) {
        return true;
      }
      // Disable dates before fromDate
      if (date.isBefore(fromDate, 'day')) {
        return true;
      }
    }
    
    return false;
  };

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      if (!token || !userId) {
        handleAuthError();
        return;
      }

      try {
        const response = await fetch(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        console.error('Error fetching business segments:', error);
      }
    };

    if (token && userId) fetchSegments();
  }, [token, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      try {
        const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setAreas(data);
          setSelectedAreaObj(null);
          setBranches([]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        console.error('Error fetching areas by segment:', error);
        setAreas([]);
        setBranches([]);
        setSelectedAreaObj(null);
        setSelectedBranchObj(null);
      }
    };

    if (token && branchType) fetchAreasBySegment();
  }, [branchType, token]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      try {
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        console.error('Error fetching branch list:', error);
        setBranches([]);
      }
    };

    if (token && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, token]);

  // Fetch SF Areas when component mounts (LEGACY - keeping for backward compatibility)
  useEffect(() => {
    const fetchAreas = async () => {
      if (!token || !userId) {
        handleAuthError();
        return;
      }

      setLoading(true);
      
      try {
        const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch SF Areas');
        }

        const data = await response.json();
        setSfAreas(data || []);
        
        // Auto-select first area if available
        if (data && data.length > 0) {
          setSfAreaId(data[0].sfAreaId);
        }
      } catch (error) {
        console.error('Error fetching SF Areas:', error);
        setError("Failed to fetch SF Areas. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (token && userId) {
      fetchAreas();
    } else {
      handleAuthError();
    }
  }, [token, userId]);

  // Fetch report data when filters change - UPDATED with new parameters
  const fetchReportData = async () => {
    // Use new area and branch selection if available, otherwise fall back to legacy
    const areaId = selectedAreaObj ? (selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId) : sfAreaId;
    const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
    
    if (!areaId) {
      setError("Please select an Area.");
      return;
    }

    if (!fromDate || !toDate) {
      setError('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      setError('From Date cannot be after To Date.');
      return;
    }

    // Note: 7-day validation is now handled in date change handlers
    // Additional validation here for safety
    const daysDifference = toDate.diff(fromDate, 'day');
    if (daysDifference > 31) {
      setError('Date range cannot exceed 31 days. Please select a shorter date range.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const fromDateStr = fromDate ? fromDate.format('YYYYMMDD') : dayjs().subtract(1, 'month').format('YYYYMMDD');
      const toDateStr = toDate ? toDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');
      
      // Use the updated day-end summary report API with new parameters
      const url = `${local_signage}/api/v1/dayendsummaryreport?AreaId=${areaId}&BranchId=${branchId}&FromDate=${fromDateStr}&ToDate=${toDateStr}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setReportData(data);
        if (data.length === 0) {
          setError("No data available for the selected filters.");
        }
      } else {
        console.error("Unexpected data format:", data);
        setError("Received invalid data format from server.");
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setError(`Failed to fetch report data: ${error.message}`);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on filter text
  const filteredData = reportData.filter(item => {
    if (!filterText) return true;
    
    const searchText = filterText.toLowerCase();
    return (
      (item.BranchID && item.BranchID.toLowerCase().includes(searchText)) || 
      (item.BranchName && item.BranchName.toLowerCase().includes(searchText))
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [reportData, filterText]);

  // Reset to first page when search results change
  useEffect(() => {
    if (filterText || filteredData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [filterText, filteredData.length, rowsPerPage, currentPage]);

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      setError("No data to export.");
      return;
    }

    try {
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();

      // Create headers row (match frontend)
      const headers = [
        "Sr. No.",
        "Branch ID",
        "Branch Name",
        "Business Date",
        "Opening Time",
        "Day End Time",
        "TAT"
      ];

      // Create metadata rows
      const metadata = [
        [`Business Date: ${formatDisplayDate(businessDate)}`],
        [`SF Area: ${sfAreas.find(area => area.sfAreaId === sfAreaId)?.sfAreaName || sfAreaId}`],
        [""] // Empty row for spacing
      ];

      // TAT calculation function (same as frontend)
      const getTAT = (open, close) => {
        if (!open || !close) return '-';
        const parse = (str) => {
          const [dayMonth, time] = str.split(' ');
          if (!dayMonth || !time) return null;
          const [day, mon] = dayMonth.split('-');
          const [hour, min] = time.split(':');
          const monthMap = {
            Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
            Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
          };
          const now = new Date();
          const year = now.getFullYear();
          return new Date(year, monthMap[mon], parseInt(day), parseInt(hour), parseInt(min));
        };
        const openDate = parse(open);
        const closeDate = parse(close);
        if (!openDate || !closeDate) return '-';
        let diff = (closeDate - openDate) / 1000;
        if (diff < 0) diff += 24 * 3600;
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        return `${h}h ${m}m`;
      };

      // Convert the data to the format expected by xlsx
      const excelData = [
        ...metadata,
        headers,
        ...filteredData.map((item, index) => [
          index + 1,
          item.BranchID || '',
          item.BranchName || '',
          item.BusinessDate || '',
          item.OpeningTime || '',
          item.DayEndTime || '',
          getTAT(item.OpeningTime, item.DayEndTime)
        ])
      ];

      // Create worksheet from data
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 8 },   // Sr. No.
        { wch: 12 },  // Branch ID
        { wch: 25 },  // Branch Name
        { wch: 15 },  // Business Date
        { wch: 14 },  // Opening Time
        { wch: 14 },  // Day End Time
        { wch: 10 },  // TAT
      ];

      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Day End Summary");

      // Format date in filename using DDMMYYYY format
      const dateFormatted = formatDateForApi(businessDate);
      const sfAreaName = sfAreas.find(area => area.sfAreaId === sfAreaId)?.sfAreaName || sfAreaId;

      // Create filename
      const fileName = `day_end_summary_${sfAreaName}_${dateFormatted}.xlsx`;

      // Generate and save the file
      XLSX.writeFile(workbook, fileName);

    } catch (error) {
      console.error("Error exporting Excel file:", error);
      setError("Failed to export Excel file.");
    }
  };

  // Enhanced Loader component
  const Loader = () => (
    <div className="horecaQualify-loader-container">
      <div className="horecaQualify-loader">
        <div className="horecaQualify-loader-circle"></div>
        <div className="horecaQualify-loader-circle"></div>
        <div className="horecaQualify-loader-circle"></div>
      </div>
      <p className="horecaQualify-loader-text">Loading data...</p>
    </div>
  );

  return (
    <>
      <Header />
      <div className="horecaQualify-container">
        {loading && <Loader />}

        {/* <h2 className="horecaQualify-title">Day End Summary Report</h2> */}

        <div className="horecaQualify-controls" style={{ 
          display: 'flex', 
          flexWrap: 'nowrap', 
          gap: '8px', 
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '8px 0',
          overflowX: 'auto',
          width: '100%'
        }}>
          {/* NEW: Segment Dropdown */}
          <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 150, flex: '0 0 150px' }} required>
            <InputLabel id="segment-select-label">Select Segment</InputLabel>
            <Select
              labelId="segment-select-label"
              value={branchType}
              onChange={(e) => setBranchType(e.target.value)}
              label="Select Segment"
              disabled={loading}
              
            >
              {segments.map((segment) => (
                <MenuItem key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                  {segment.businessSegmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* NEW: Area Dropdown */}
          <Autocomplete
            options={areas}
            getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
            value={selectedAreaObj}
            onChange={(event, newValue) => {
              setSelectedAreaObj(newValue);
            }}
            disabled={!branchType || loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Area"
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 150,
                  flex: '0 0 150px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                  }
                }}
              />
            )}
          />

          {/* NEW: Branch Dropdown */}
          <Autocomplete
            options={branches}
            getOptionLabel={(option) => option.branchName || ''}
            value={selectedBranchObj}
            onChange={(event, newValue) => {
              setSelectedBranchObj(newValue);
            }}
            disabled={!selectedAreaObj || loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Branch"
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 150,
                  flex: '0 0 150px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                  }
                }}
              />
            )}
          />

          {/* From Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={handleFromDateChange}
              format="DD-MM-YYYY"
              disabled={loading}
              shouldDisableDate={shouldDisableFromDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    flex: '0 0 120px',
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* To Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={handleToDateChange}
              format="DD-MM-YYYY"
              disabled={loading}
              shouldDisableDate={shouldDisableToDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    flex: '0 0 120px',
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                      borderColor: fromDate && toDate && toDate.diff(fromDate, 'day') > 31 ? '#f44336' : undefined,
                      '&:hover': {
                        borderColor: fromDate && toDate && toDate.diff(fromDate, 'day') > 31 ? '#f44336' : undefined,
                      },
                      '&.Mui-focused': {
                        borderColor: fromDate && toDate && toDate.diff(fromDate, 'day') > 31 ? '#f44336' : undefined,
                      }
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* LEGACY: SF Area Dropdown - keeping for backward compatibility */}
          {/* <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 180, display: selectedAreaObj ? 'none' : 'inline-flex' }}>
            <InputLabel id="sfarea-select-label">Sales Force Area (Legacy)</InputLabel>
            <Select
              labelId="sfarea-select-label"
              value={sfAreaId}
              onChange={(e) => setSfAreaId(e.target.value)}
              label="Sales Force Area (Legacy)"
              disabled={loading || selectedAreaObj}
            >
              {sfAreas.map((area) => (
                <MenuItem key={area.sfAreaId} value={area.sfAreaId}>
                  {area.sfAreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}

          {/* Search Field */}
          <div className="horecaQualify-search" style={{ minWidth: '150px', flex: '0 0 150px' }}>
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Search branches..."
              className="horecaQualify-search-field"
              disabled={loading}
              style={{ width: '100%', height: '40px', fontSize: '14px' }}
            />
          </div>
          
          {/* Buttons */}
          <div className="horecaQualify-buttons" style={{ display: 'flex', gap: '8px', flex: '0 0 auto' }}>
            <button
              onClick={fetchReportData}
              className="horecaQualify-button horecaQualify-load-button"
              disabled={loading || !selectedAreaObj || !selectedBranchObj || !isDateRangeValid()}
              style={{ whiteSpace: 'nowrap', padding: '8px 12px', fontSize: '14px' }}
            >
              Load Report
            </button>

            <button
              onClick={exportToExcel}
              className="horecaQualify-button horecaQualify-export-button"
              disabled={loading || filteredData.length === 0}
              style={{ whiteSpace: 'nowrap', padding: '8px 12px', fontSize: '14px' }}
            >
              Export
            </button>
          </div>
        </div>

        {error && <div className="horecaQualify-error-message">{error}</div>}

        {/* Results Summary */}
        {filteredData.length > 0 && (
          <div className="horecaQualify-results-summary" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '5px 0',
            borderBottom: '1px solid #e0e0e0',
            marginBottom: '5px'
          }}>
            <span>Showing {paginatedData.length} of {filteredData.length} records</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label>Rows per page:</label>
              <select 
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          </div>
        )}

        <div className="horecaQualify-table-container">
          <table className="horecaQualify-report">

<thead>
  <tr>
    <th>Sr. No.</th>
    <th>Branch ID</th>
    <th>Branch Name</th>
    <th>Business Date</th>
    <th>Opening Time</th>
    <th>Day End Time</th>
    <th>TAT</th>
    {/* <th>Inward Qty</th>
    <th>Sale Qty</th>
    <th>Sale Amount</th> */}
  </tr>
</thead>
<tbody>
  {paginatedData.length > 0 ? (
    paginatedData.map((item, index) => {
      // Calculate TAT (Turn Around Time) as difference between DayEndTime and OpeningTime
      // Both times are in format like "29-Jun 15:55"
      const getTAT = (open, close) => {
        if (!open || !close) return '-';
        // Parse "DD-MMM HH:mm"
        const parse = (str) => {
          const [dayMonth, time] = str.split(' ');
          if (!dayMonth || !time) return null;
          const [day, mon] = dayMonth.split('-');
          const [hour, min] = time.split(':');
          const monthMap = {
            Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
            Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
          };
          const now = new Date();
          const year = now.getFullYear(); // Assume current year
          return new Date(year, monthMap[mon], parseInt(day), parseInt(hour), parseInt(min));
        };
        const openDate = parse(open);
        const closeDate = parse(close);
        if (!openDate || !closeDate) return '-';
        let diff = (closeDate - openDate) / 1000; // seconds
        if (diff < 0) diff += 24 * 3600; // handle next day
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        return `${h}h ${m}m`;
      };
      return (
        <tr key={index} className={index % 2 === 0 ? "horecaQualify-row-even" : "horecaQualify-row-odd"}>
          <td>{startIndex + index + 1}</td>
          <td>{item.BranchID || '-'}</td>
          <td>{item.BranchName || '-'}</td>
          <td>{item.BusinessDate || '-'}</td>
          <td>{item.OpeningTime || '-'}</td>
          <td>{item.DayEndTime || '-'}</td>
          <td>{getTAT(item.OpeningTime, item.DayEndTime)}</td>
          {/* <td className="horecaQualify-currency">
            {typeof item.InWardQty === 'number' 
              ? item.InWardQty.toLocaleString('en-IN') 
              : '0'}
          </td>
          <td className="horecaQualify-currency">
            {typeof item.SaleQty === 'number' 
              ? item.SaleQty.toLocaleString('en-IN') 
              : '0'}
          </td>
          <td className="horecaQualify-currency">
            ₹{typeof item.SaleAmount === 'number' 
              ? item.SaleAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
              : '0.00'}
          </td> */}
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan="7" className="horecaQualify-no-data">
        {reportData.length === 0 && !error 
          ? "Click 'Load Report' to view data" 
          : reportData.length === 0 && error 
            ? "No data available" 
            : "No data matches your search criteria"}
      </td>
    </tr>
  )}
</tbody>

          </table>
        </div>

        {/* Pagination Controls */}
        {filteredData.length > 0 && !loading && (
          <div className="horecaQualify-pagination" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            padding: '2px 0',
            borderTop: '1px solid #e0e0e0',
            marginTop: '2px'
          }}>
            {/* First Page Button */}
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: currentPage === page ? '#007bff' : '#fff',
                      color: currentPage === page ? '#fff' : '#000',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: currentPage === page ? 'bold' : 'normal'
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Last
            </button>
            
            {/* Page Info */}
            <span style={{ marginLeft: '16px', fontSize: '14px', color: '#666' }}>
              Page {currentPage} of {totalPages}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

Posdayend.propTypes = {
  // No specific props needed for this component
};

export default Posdayend;